import React, { useRef, useState, useEffect } from "react";
import { cx } from "../styles";
import type { DataBinding } from "../dashboard/spec";

// Dashboard configuration types
export interface ResponsivePosition {
  desktop: { x: number; y: number };
  tablet: { x: number; y: number };
  mobile: { x: number; y: number };
}

export interface ResponsiveSize {
  desktop: { width: number; height: number };
  tablet: { width: number; height: number };
  mobile: { width: number; height: number };
}

export interface ResponsiveVisibility {
  desktop: boolean;
  tablet: boolean;
  mobile: boolean;
}

export interface ResponsiveLayout {
  desktop: { width: number; height: number };
  tablet: { width: number; height: number };
  mobile: { width: number; height: number };
}

export interface DashboardComponent {
  id: string;
  type: "label" | "chart" | "kpi" | "gauge" | "table" | "text" | "image";
  // Support both legacy and new responsive formats
  position: ResponsivePosition | { x: number; y: number };
  size: ResponsiveSize | { width: number; height: number };
  visibility?: ResponsiveVisibility;
  props: {
    text?: string;
    fontSize?: number;
    color?: string;
    alignment?: string;
    chartType?: string;
    title?: string;
    series?: any[];
    src?: string;
    alt?: string;
    fit?: string;
    /** Optional live/historic data binding (source + channels). */
    dataBinding?: DataBinding;
    [key: string]: any;
  };
  style: Record<string, any>;
}

export interface DashboardConfig {
  components: DashboardComponent[];
  // Support both legacy and new responsive layouts
  layout: ResponsiveLayout | { width: number; height: number };
}

export interface ImportedDashboard {
  id: string;
  name: string;
  description: string;
  config: DashboardConfig;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  metadata: Record<string, any>;
}

// Utility functions for responsive dashboard handling
export type Breakpoint = "desktop" | "tablet" | "mobile";

export function getCurrentBreakpoint(): Breakpoint {
  const width = window.innerWidth;
  if (width >= 1024) return "desktop";
  if (width >= 768) return "tablet";
  return "mobile";
}

export function getResponsiveValue<T>(
  value: T | { desktop: T; tablet: T; mobile: T },
  breakpoint: Breakpoint = getCurrentBreakpoint(),
): T {
  if (typeof value === "object" && value !== null && "desktop" in value) {
    return (value as { desktop: T; tablet: T; mobile: T })[breakpoint];
  }
  return value as T;
}

export function getComponentPosition(
  component: DashboardComponent,
  breakpoint: Breakpoint = getCurrentBreakpoint(),
): { x: number; y: number } {
  return getResponsiveValue(component.position, breakpoint);
}

export function getComponentSize(
  component: DashboardComponent,
  breakpoint: Breakpoint = getCurrentBreakpoint(),
): { width: number; height: number } {
  return getResponsiveValue(component.size, breakpoint);
}

export function getComponentVisibility(
  component: DashboardComponent,
  breakpoint: Breakpoint = getCurrentBreakpoint(),
): boolean {
  if (!component.visibility) return true;
  return getResponsiveValue(component.visibility, breakpoint);
}

export function getLayoutDimensions(
  layout: ResponsiveLayout | { width: number; height: number },
  breakpoint: Breakpoint = getCurrentBreakpoint(),
): { width: number; height: number } {
  return getResponsiveValue(layout, breakpoint);
}

interface DashboardImporterProps {
  onImport: (dashboard: ImportedDashboard) => void;
  onError?: (error: string) => void;
  className?: string;
  children?: React.ReactNode;
}

const DashboardImporter: React.FC<DashboardImporterProps> = ({
  onImport,
  onError,
  className,
  children,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = e.target?.result as string;
        const dashboard = JSON.parse(jsonContent) as ImportedDashboard;

        // Validate the imported dashboard structure
        if (!dashboard.config || !dashboard.config.components) {
          throw new Error(
            "Invalid dashboard format: missing config or components",
          );
        }

        if (!Array.isArray(dashboard.config.components)) {
          throw new Error(
            "Invalid dashboard format: components must be an array",
          );
        }

        // Validate each component
        for (const component of dashboard.config.components) {
          if (
            !component.id ||
            !component.type ||
            !component.position ||
            !component.size
          ) {
            throw new Error(
              `Invalid component format: ${component.id || "unknown"}`,
            );
          }

          // Validate position format (responsive or legacy)
          const position = component.position;
          if ("desktop" in position) {
            // Responsive format validation
            if (!position.desktop || !position.tablet || !position.mobile) {
              throw new Error(
                `Invalid responsive position format for component: ${component.id}`,
              );
            }
            if (
              typeof position.desktop.x !== "number" ||
              typeof position.desktop.y !== "number"
            ) {
              throw new Error(
                `Invalid position coordinates for component: ${component.id}`,
              );
            }
          } else {
            // Legacy format validation
            if (
              typeof position.x !== "number" ||
              typeof position.y !== "number"
            ) {
              throw new Error(
                `Invalid position coordinates for component: ${component.id}`,
              );
            }
          }

          // Validate size format (responsive or legacy)
          const size = component.size;
          if ("desktop" in size) {
            // Responsive format validation
            if (!size.desktop || !size.tablet || !size.mobile) {
              throw new Error(
                `Invalid responsive size format for component: ${component.id}`,
              );
            }
            if (
              typeof size.desktop.width !== "number" ||
              typeof size.desktop.height !== "number"
            ) {
              throw new Error(
                `Invalid size dimensions for component: ${component.id}`,
              );
            }
          } else {
            // Legacy format validation
            if (
              typeof size.width !== "number" ||
              typeof size.height !== "number"
            ) {
              throw new Error(
                `Invalid size dimensions for component: ${component.id}`,
              );
            }
          }
        }

        // Validate layout (responsive or legacy)
        const layout = dashboard.config.layout;
        if (!layout) {
          throw new Error("Invalid dashboard format: missing layout");
        }

        if ("desktop" in layout) {
          // Responsive layout validation
          if (!layout.desktop || !layout.tablet || !layout.mobile) {
            throw new Error(
              "Invalid dashboard format: missing responsive layout dimensions",
            );
          }
          if (!layout.desktop.width || !layout.desktop.height) {
            throw new Error(
              "Invalid dashboard format: invalid responsive layout dimensions",
            );
          }
        } else {
          // Legacy layout validation
          if (!layout.width || !layout.height) {
            throw new Error(
              "Invalid dashboard format: missing layout dimensions",
            );
          }
        }

        console.log("Dashboard imported successfully:", dashboard);
        onImport(dashboard);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        console.error("Error importing dashboard:", error);

        if (onError) {
          onError(errorMessage);
        } else {
          alert(`Error importing dashboard: ${errorMessage}`);
        }
      }
    };

    reader.onerror = () => {
      const errorMessage = "Error reading file";
      console.error("FileReader error");

      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
    };

    reader.readAsText(file);

    // Reset file input
    event.target.value = "";
  };

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {/* Render children with click handler, or default button */}
      {children ? (
        <div onClick={handleImportClick} className={className}>
          {children}
        </div>
      ) : (
        <button
          onClick={handleImportClick}
          className={cx(
            "px-4 py-2 rounded-md transition-colors",
            "bg-orange-600 text-white hover:bg-orange-700",
            "focus:outline-none focus:ring-2 focus:ring-orange-500",
            className,
          )}
        >
          <span>📥</span> Import Dashboard
        </button>
      )}
    </>
  );
};

export default DashboardImporter;

// Custom hook for responsive dashboard handling
export function useResponsiveDashboard() {
  const [breakpoint, setBreakpoint] =
    useState<Breakpoint>(getCurrentBreakpoint);

  useEffect(() => {
    const handleResize = () => {
      setBreakpoint(getCurrentBreakpoint());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    breakpoint,
    getCurrentBreakpoint,
    getResponsiveValue: function <T>(
      value: T | { desktop: T; tablet: T; mobile: T },
    ): T {
      return getResponsiveValue(value, breakpoint);
    },
    getComponentPosition: (component: DashboardComponent) =>
      getComponentPosition(component, breakpoint),
    getComponentSize: (component: DashboardComponent) =>
      getComponentSize(component, breakpoint),
    getComponentVisibility: (component: DashboardComponent) =>
      getComponentVisibility(component, breakpoint),
    getLayoutDimensions: (
      layout: ResponsiveLayout | { width: number; height: number },
    ) => getLayoutDimensions(layout, breakpoint),
  };
}
